import { Button, type ButtonProps, CopyButton, Stack, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/hooks"
import { baseURL } from "@/utils/env"
import { icons } from "../utils"
import { useAppModal } from "./ModalBase"

interface IProps extends ButtonProps {
  path: string
}

// Calendars only subscribe to webcal links, a plain http link downloads the file instead
export const buildWebcalLink = (url: string) => url.replace(/^https?:\/\//, "webcal://")
export const buildGoogleLink = (url: string) =>
  `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(buildWebcalLink(url))}`

export function CalendarButton({ path, ...props }: IProps) {
  const { token } = useAuth()
  const { t } = useTranslation()
  const { open } = useAppModal()

  const icalLink = `${baseURL}/api/v1${path}?token=${token}`

  return (
    <Button
      {...props}
      color="gray"
      leftSection={<icons.Calendar />}
      variant="outline"
      onClick={() =>
        open({
          title: t("Subscribe to calendar"),
          children: (
            <Stack>
              <Text size="sm" c="dimmed">
                {t("The calendar updates automatically. The link is private, do not share it.")}
              </Text>
              <CopyButton value={icalLink}>
                {({ copied, copy }) => (
                  <Button
                    color="gray"
                    variant={copied ? "filled" : "outline"}
                    leftSection={<icons.Copy size={16} />}
                    onClick={copy}
                  >
                    {copied ? t("Copied") : t("Copy link")}
                  </Button>
                )}
              </CopyButton>
              <Button component="a" href={buildWebcalLink(icalLink)} leftSection={<icons.Apple size={16} />}>
                {t("Add to Apple Calendar")}
              </Button>
              <Button
                component="a"
                href={buildGoogleLink(icalLink)}
                target="_blank"
                leftSection={<icons.Google size={16} />}
              >
                {t("Add to Google Calendar")}
              </Button>
            </Stack>
          ),
        })
      }
    >
      {t("Subscribe to calendar")}
    </Button>
  )
}
